import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, skip } from 'rxjs/operators';
import { IconStatus } from 'src/app/angular-core/interfaces/list-item.interfaces';
import { hasRoleUserData } from 'src/app/utils/functions/permission.function';
import { Roles } from 'src/app/utils/enums/roles';
import { IUserData } from 'src/app/utils/interfaces/auth.interfaces';
import { selectUserData } from 'src/app/store/selectors/auth.selectors';
import { DialogAnimationService } from 'src/app/angular-core/services/dialog-animation.service';
import { Actions, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { RemoteMaintenanceItemInfoComponent } from './remote-maintenance-item-info/remote-maintenance-item-info.component';
import { IPermission } from 'src/app/shared/guards/authorized.guard';
import { OperationEnum } from 'src/app/utils/enums/operation.enums';
import { getXpacInfoStringAddressAndRetailer, getXpacInfoStringIdAndInstallationDate } from 'src/app/utils/functions/xpac.functions';
import { LocalizedDatePipe } from 'src/app/angular-core/utils/pipes/localized-date/localized-date.pipe';
import { BasicXpacInfo, Company, ControllerTypeFilter, DeviceSortOrder } from 'src/app/generated/CrossPoint.RemoteXpac.Server';
import { getDevicesAction, DevicesActionsTypes } from 'src/app/store/actions/devices.actions';
import { IRemoteXPACPortalState } from 'src/app/utils/interfaces/store.interfaces';
import { selectDevices } from 'src/app/store/selectors/devices.selectors';
import { getTranslationKeyForValue } from 'src/app/angular-core/utils/functions/translation.functions';
import { XpacFilter } from 'src/app/utils/enums/xpac-filter.enum';
import { ListValueSelectorComponent, ValueOption } from 'src/app/angular-core/components/list-value-selector/list-value-selector.component';
import { EnumTranslationMap } from './enum-translation-map';
import { CompanyType } from 'src/app/angular-core/services/auth.service';
import { CompanyFilter, CompanyFilterComponent } from 'src/app/shared/components/company-filter/company-filter.component';
import { selectCompanyCache } from 'src/app/store/selectors/identitymanagment.selector';
import { hasValue } from 'src/app/angular-core/utils/functions/common.functions';


interface Settings {
  searchText: string;         // Last used search text
  sortOrder: DeviceSortOrder; // Last used device sort order
  filterText: string;         // Last used "predefined filters" string
  companyId: string;          // Last used company ID to filter on, could be empty
  companyName: string;        // Name of the company with companyId, cached for easier page reloads
  controllerType: ControllerTypeFilter; // Last used controller type filter
}

@UntilDestroy()
@Component({
  selector: 'xpac-remote-maintenance',
  templateUrl: './remote-maintenance.component.html',
  styleUrls: ['./remote-maintenance.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class RemoteMaintenanceComponent implements OnInit {
  private static readonly MAX_ITEMS_PER_REQUEST: number = 25;
  private static readonly DEVICE_CONNECTED_ICON: IconStatus = { icon: 'cloud_done', class: 'connect-icon' };
  private static readonly DEVICE_CONNECTED_NO_LICENSE_ICON: IconStatus = { icon: 'cloud', class: 'connect-icon' };
  private static readonly DEVICE_DISCONNECTED_ICON: IconStatus = { icon: 'cloud_off', class: 'disconnect-icon' };
  private static readonly DEVICE_DELETED_ICON: IconStatus = { icon: 'delete', class: 'disconnect-icon' };

  private userData: IUserData = null;

  public filteredLastInstalledDevices: BasicXpacInfo[] = [];
  public isLoading: boolean = true;
  public isLoadingFilteredDevices: boolean = false;
  public isLoadingMore: boolean = false;
  public hasMore: boolean = false;

  public controllerType: ControllerTypeFilter = ControllerTypeFilter.All;
  public sortOrder: DeviceSortOrder = DeviceSortOrder.InstallationDate;
  public searchText: string = '';
  public filterDisplayString: string = '';

  private filterText: string = '';
  private filterTranslation: EnumTranslationMap<XpacFilter>;
  private filterCompanyId: string = '';
  private filterCompanyName: string = '';
  private companyCache: ReadonlyMap<string, Company>;

  public get sortOptions(): DeviceSortOrder[] {
    return Object.values(DeviceSortOrder);
  }

  public get controllerTypeOptions(): ControllerTypeFilter[] {
    return Object.values(ControllerTypeFilter);
  }

  constructor(private store: Store<IRemoteXPACPortalState>,
    private cdr: ChangeDetectorRef,
    private datePipe: LocalizedDatePipe,
    private translate: TranslateService,
    public dialog: DialogAnimationService,
    private actionsListener$: Actions) { }

  public ngOnInit(): void {
    this.filterTranslation = new EnumTranslationMap('Modules.RemoteMaintenance.Filters.', this.translate, XpacFilter);
    const settings = this.loadSettings();
    this.searchText = settings.searchText;
    this.controllerType = settings.controllerType;
    this.sortOrder = settings.sortOrder;
    this.filterText = settings.filterText;
    this.filterDisplayString = this.filterTranslation.getCommaSeparatedStringOrNone(this.parseFilterText(this.filterText));
    this.filterCompanyId = settings.companyId;
    this.filterCompanyName = settings.companyName;
    this.initData();
  }

  public agGetRequiredPermissions(): IPermission[] {
    return [
      { type: 'Device_RemoteAccess', operation: OperationEnum.ACTION }
    ];
  }

  public remoteXPACInfo( xpac: BasicXpacInfo): void {
    this.dialog.open(RemoteMaintenanceItemInfoComponent, {
      panelClass: 'my-full-screen-dialog',
      data: { xpac: xpac },
      animation: { to: "left" }
    });
  }

  public getTitleString(xpac: BasicXpacInfo): string {
    let title: string = "";

    if(xpac.storeName !== ""){
      title = xpac.storeName;
    }
    else if(xpac.deviceId !== ""){
      title = xpac.deviceId;
    }
    else{
      // default no name
      title = this.translate.instant("Modules.RemoteMaintenance.NoName");
    }

    return title;
  }

  public getXpacInfoStringIdAndInstallationDate(xpac: BasicXpacInfo): string {
    return getXpacInfoStringIdAndInstallationDate(xpac, this.datePipe);
  }

  public getXpacInfoStringAddressAndRetailer(xpac: BasicXpacInfo): string {
    return getXpacInfoStringAddressAndRetailer(xpac, hasRoleUserData(Roles.Crosspoint, this.userData));
  }

  public getProblemsNumber(xpac: BasicXpacInfo): number {
    if (xpac.isConnected) {
      return xpac.problemCount;
    }
    else {
      return 0;
    }
  }

  public async onSearchTextChanged(searchText: string): Promise<void> {
    this.searchText = searchText;

    while (this.isLoadingFilteredDevices) {
      // Wait a little so an previous call can be dispatched first.
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    this.refreshData();
  }

  public getStatusIcon(device: BasicXpacInfo): IconStatus[] {
    if (device.isConnected) {
      if(device.hasSubscription){
        return [RemoteMaintenanceComponent.DEVICE_CONNECTED_ICON];
      }
      else{
        return [RemoteMaintenanceComponent.DEVICE_CONNECTED_NO_LICENSE_ICON];
      }
    }
    else if (device.isDeleted) {
      return [RemoteMaintenanceComponent.DEVICE_DELETED_ICON];
    }

    return [RemoteMaintenanceComponent.DEVICE_DISCONNECTED_ICON];
  }

  public getListInfoText(): string {
    return this.translate.instant(`Modules.RemoteMaintenance.SortOrdersTitle.${getTranslationKeyForValue(this.sortOrder)}`);
  }

  public getCompanyDisplayName(): string {
    if (this.filterCompanyId.length < 1) {
      // No company filter is active
      return this.translate.instant('Modules.RemoteMaintenance.NoCompanyName');
    }

    // Usually the company should be in the cache, as the user opening the company filter dialog will populate it
    const company = this.companyCache.get(this.filterCompanyId);
    if (!company) {
      if (this.filterCompanyName.length > 0) {
        // This can happen on initial page load when we have not loaded companies from the cloud yet so companyCache is empty,
        // but we do have old filter settings cached in the browser.
        return this.filterCompanyName;
      }

      // Abolutely no clue what company this is, showing an error is fine
      return '#ERROR';
    }
    return company.description;
  }

  public canFilterByCompany(): boolean {
    return this.userData.companyType !== CompanyType.Retailer;
  }

  public refreshData(): void {
    this.isLoadingFilteredDevices = true;
    this.cdr.markForCheck();

    let combinedText = this.filterText;
    if (combinedText.length > 0) {
      combinedText += " ";
    }
    if (this.filterCompanyId.length > 0) {
      combinedText += `/company:${this.filterCompanyId} `;
    }
    combinedText += this.searchText;

    this.store.dispatch(getDevicesAction({
      data: {
        type: this.controllerType,
        search: combinedText,
        order: this.sortOrder,
        maxItems: RemoteMaintenanceComponent.MAX_ITEMS_PER_REQUEST + 1,
      }
    }));
    this.saveSettings({
      companyId: this.filterCompanyId,
      companyName: this.filterCompanyName,
      searchText: this.searchText,
      sortOrder: this.sortOrder,
      filterText: this.filterText,
      controllerType: this.controllerType,
    });
  }

  public loadMore(): void {
    this.isLoadingMore = true;
    this.cdr.markForCheck();

    let combinedText = this.filterText;
    if (combinedText.length > 0) {
      combinedText += " ";
    }
    if (this.filterCompanyId.length > 0) {
      combinedText += `/company:${this.filterCompanyId} `;
    }
    combinedText += this.searchText;

    this.store.dispatch(getDevicesAction({
      data: {
        type: this.controllerType,
        search: combinedText,
        order: this.sortOrder,
        skip: this.filteredLastInstalledDevices.length,
        maxItems: RemoteMaintenanceComponent.MAX_ITEMS_PER_REQUEST + 1,
      }
    }));
  }

  public openCompanyDialog(): void {
    const dialogOptions: CompanyFilter = {
      companyTypes: [ CompanyType.Dealer, CompanyType.Retailer ],
      includeNoneOption: true,
      filteredCompaniesTitle: this.translate.instant('Modules.RemoteMaintenance.CompanyFilterInfo'),
      header: this.translate.instant('Modules.RemoteMaintenance.CompanyTitle'),
    };
    const dialogRef = this.dialog.open(CompanyFilterComponent, {
      panelClass: 'my-full-screen-dialog',
      data: dialogOptions,
      animation: { to: "left" }
    });
    dialogRef.afterClosed().subscribe((result: CompanyFilter) => {
      if (!result || !result.selectedCompany) {
        // Deal with undefined being returned when the user presses escape or presses back without selecting something
        return;
      }

      const selectedCompanyId = result.selectedCompany?.companyId ?? '';
      if (this.filterCompanyId === selectedCompanyId) {
        // No changes
        this.cdr.markForCheck();
        return;
      }

      // Update the filters and refresh the list
      this.filterCompanyId = selectedCompanyId;
      this.filterCompanyName = result.selectedCompany?.description ?? '';
      this.refreshData();
    });
  }

  public openFilterDialog(): void {
    const existingFilters = this.parseFilterText(this.filterText);
    const textOptions: ValueOption[] = this.filterTranslation.getAllTranslations().map(textOption => ({
      value: textOption
    }));
    const selectedTextOptions = this.filterTranslation.getTranslations(existingFilters);

    const dialogRef = this.dialog.open(ListValueSelectorComponent, {
      panelClass: 'my-full-screen-dialog',
      data: {
        options: textOptions,
        header: this.translate.instant('Modules.RemoteMaintenance.FiltersTitle'),
        useMultiSelection: true,
        headerInfo: this.translate.instant('Modules.RemoteMaintenance.FiltersInfo'),
        selectedOptions: selectedTextOptions,
        showSelectAllButton: false,
      },
      animation: { to: "left" }
    });
    dialogRef.afterClosed().subscribe((result: string[]) => {
      if (!result) {
        // Deal with undefined being returned when the user presses escape
        return;
      }

      const newFilters = this.filterTranslation.getEnums(result);
      const newFilterText = this.createFilterText(newFilters);
      if (newFilterText === this.filterText) {
        // No changes, no need to reload the list
        return;
      }

      this.filterText = newFilterText;
      this.filterDisplayString = this.filterTranslation.getCommaSeparatedStringOrNone(newFilters);
      this.refreshData();
    });
  }

  private initData(): void {
    this.store.select(selectUserData)
      .pipe(
        filter((value: IUserData | null) => value !== null),
        untilDestroyed(this)
      )
      .subscribe((userData: IUserData) => {
       this.userData = userData;
       this.initListeners();
      });

    this.store.select(selectCompanyCache)
      .pipe(
        filter(hasValue),
        untilDestroyed(this)
      )
      .subscribe((cache) => {
        this.companyCache = cache;
        this.cdr.markForCheck();
      });
  }

  private initListeners(): void {
    // If a device is deleted refresh the data so we have an up to date list.
    this.actionsListener$
      .pipe(ofType(DevicesActionsTypes.DELETE_DEVICE_SUCCESS))
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.refreshData();
      });

    this.store.select(selectDevices)
      .pipe(
        // Skip the current store value so we will not display wrong data
        skip(1),
        filter<BasicXpacInfo[]>(Boolean),
        untilDestroyed(this)
      )
      .subscribe((xpacs: BasicXpacInfo[]) => {
        if (!this.isLoadingMore) {
          this.filteredLastInstalledDevices = [];
        }
        this.hasMore = xpacs.length > RemoteMaintenanceComponent.MAX_ITEMS_PER_REQUEST;
        xpacs = xpacs.slice(0, RemoteMaintenanceComponent.MAX_ITEMS_PER_REQUEST);

        this.filteredLastInstalledDevices.push(...xpacs);
        this.isLoading = false;
        this.isLoadingFilteredDevices = false;
        this.isLoadingMore = false;
        this.cdr.markForCheck();
      });

    // Load the initial data
    this.refreshData();
  }

  private loadSettings(): Settings {
    let settings: Partial<Settings>;
    try {
      settings = JSON.parse(window.sessionStorage.getItem('service-list'));
    }
    catch {
    }
    return {
      searchText: settings?.searchText ?? '',
      sortOrder: settings?.sortOrder ?? DeviceSortOrder.InstallationDate,
      // Run through the parsing logic to ensure only valid filter values are loaded
      filterText: this.createFilterText(this.parseFilterText(settings?.filterText ?? '')),
      companyId: settings?.companyId ?? '',
      companyName: settings?.companyName ?? '',
      controllerType: settings?.controllerType ?? ControllerTypeFilter.All,
    };
  }

  private saveSettings(settings: Settings): void {
    window.sessionStorage.setItem('service-list', JSON.stringify(settings));
  }

  private parseFilterText(filterText: string): XpacFilter[] {
    const result: XpacFilter[] = [];
    const filters = filterText.split(' ');
    for (let filter of filters) {
      filter = filter.trim();
      if (!filter.startsWith('/')) {
        continue;
      }
      filter = filter.replace('/', '');
      if (Object.values(XpacFilter).includes(filter as XpacFilter)) {
        result.push(filter as XpacFilter);
      }
    }
    return result;
  }

  private createFilterText(filters: XpacFilter[]): string {
    return filters.map(f => `/${f}`).join(' ');
  }
}
